import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Heart, Users, Target, Sparkles, Mail, ChevronDown } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useEffect } from 'react';

const COMMUNITY_IMG_2 = "https://customer-assets.emergentagent.com/job_0e3427ab-d5b3-4fa8-993a-18bab96ac733/artifacts/8hosln5z_IMG_6516-compressed.jpeg";
const VOLUNTEERS_IMG = "https://customer-assets.emergentagent.com/job_0e3427ab-d5b3-4fa8-993a-18bab96ac733/artifacts/jkg8o685_IMG_5732-compressed.jpeg";

const boardMembers = [
  {
    name: 'Chelsea Flynn',
    role: 'Chair',
    bio: 'Autism parent and passionate advocate for neurodiversity acceptance in the Cayman Islands.',
    image: '/chelsea.png'
  },
  {
    name: 'Davina Daley',
    role: 'Treasurer and Director',
    bio: 'Dedicated to ensuring financial sustainability and growth of Autism Acceptance initiatives.',
    image: '/davina.jpeg'
  },
  {
    name: 'Collin Anglin',
    role: 'Director',
    bio: 'Committed to building inclusive communities and supporting autism families in Cayman.',
    image: '/collin.jpeg'
  }
];

// Fundraising data
const FUNDRAISING_GOAL = 60000;
const FUNDRAISING_RAISED = 0;

export default function About() {
  const progressPercent = (FUNDRAISING_RAISED / FUNDRAISING_GOAL) * 100;
  const location = useLocation();
  
  // Handle hash navigation for board members section
  useEffect(() => {
    const hash = location.hash;
    if (hash) {
      setTimeout(() => {
        const element = document.getElementById(hash.replace('#', ''));
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [location]);

  const scrollToContent = () => {
    const element = document.getElementById('story');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero - Created by Parents */}
      <section id="story" className="bg-gradient-to-br from-[hsl(346,85%,95%)] via-background to-[hsl(27,100%,95%)] py-12 md:py-16 scroll-mt-20">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-[hsl(346,85%,90%)] text-[hsl(346,85%,35%)]">About Us</Badge>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Created by Autism Parents, For Our Community
              </h1>
              <p className="text-lg text-muted-foreground mb-6">
                Autism Acceptance is a non-profit organisation born from the hearts of autism parents, 
                neurodivergent individuals, and dedicated allies who saw a gap: a lack of safe spaces, 
                true inclusion, and understanding for the autistic community in the Cayman Islands.
              </p>
              <p className="text-muted-foreground mb-6">
                Registered Non-Profit Organisation of the Cayman Islands, NP-693
              </p>
              {/* Scroll Down Arrow */}
              <button 
                onClick={scrollToContent}
                className="flex flex-col items-center gap-2 text-muted-foreground hover:text-primary transition-colors animate-bounce"
              >
                <span className="text-sm">Learn more about us</span>
                <ChevronDown className="h-6 w-6" />
              </button>
            </div>
            <div>
              <img 
                src={VOLUNTEERS_IMG} 
                alt="Autism Acceptance team and volunteers" 
                className="rounded-2xl shadow-lg w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section-spacing">
        <div className="container-main">
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="card-base">
              <CardHeader>
                <div className="w-12 h-12 rounded-xl bg-[hsl(346,85%,90%)] flex items-center justify-center mb-4">
                  <Target className="h-6 w-6 text-[hsl(346,85%,45%)]" />
                </div>
                <CardTitle className="text-2xl">Our Mission</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  To empower autistic individuals in the Cayman Islands by fostering acceptance, 
                  promoting inclusivity, providing essential resources and support, educating the 
                  community to create a culture of understanding, and advocating for them and their families.
                </p>
              </CardContent>
            </Card>
            <Card className="card-base">
              <CardHeader>
                <div className="w-12 h-12 rounded-xl bg-[hsl(346,100%,90%)] flex items-center justify-center mb-4">
                  <Sparkles className="h-6 w-6 text-[hsl(346,100%,55%)]" />
                </div>
                <CardTitle className="text-2xl">Our Vision</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  A Cayman Islands where autistic individuals are celebrated, supported, and fully 
                  included in every corner of community life: in schools, workplaces, healthcare, 
                  and social settings.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why We Created This Hub */}
      <section className="section-spacing bg-muted/30">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center">
            <Badge className="mb-4 bg-[hsl(27,100%,90%)] text-[hsl(27,100%,35%)]">Our Story</Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Why We Created This Hub</h2>
            <p className="text-lg text-muted-foreground mb-6">
              The spark came from lived experience: parents, neurodivergent individuals, and allies 
              united by the daily realities of stigma, limited resources, and exclusion. Despite a 
              large autistic community in Cayman, too many are not accepted.
            </p>
            <p className="text-muted-foreground mb-6">
              We created this Resources Hub because we wished it existed when we started our own journeys. 
              A central place where families could find information, services, community support, and 
              practical tools, all from a neurodiversity-affirming perspective.
            </p>
            <p className="text-muted-foreground">
              This hub is our contribution to making sure no family feels as alone as we once did.
            </p>
          </div>
        </div>
      </section>

      {/* Community Centre Goal */}
      <section id="community-centre" className="section-spacing bg-[hsl(346,85%,45%)] text-white">
        <div className="container-main">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="bg-[hsl(346,100%,65%)] text-white border-none mb-4">Major Fundraising Goal</Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Cayman's First Neurodivergent Community Centre
            </h2>
            <p className="text-white/90 text-lg mb-8">
              We are raising vital funds to establish a safe and welcoming space designed for 
              autistic individuals to learn, connect, and thrive. The centre will include:
            </p>
            <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {['Sensory Room', 'Educational Workshops', 'Social Support Resources', 'Family Respite'].map((item) => (
                <div key={item} className="bg-white/10 rounded-xl p-4">
                  <p className="font-semibold">{item}</p>
                </div>
              ))}
            </div>
            
            {/* Progress Bar */}
            <div className="max-w-xl mx-auto bg-white/10 rounded-2xl p-8 mb-8">
              <div className="space-y-4">
                <div className="flex justify-between text-lg">
                  <span>Raised: ${FUNDRAISING_RAISED.toLocaleString()}</span>
                  <span>Goal: ${FUNDRAISING_GOAL.toLocaleString()}</span>
                </div>
                <div className="h-6 bg-white/20 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-[hsl(346,100%,65%)] rounded-full transition-all duration-500"
                    style={{ width: `${Math.max(progressPercent, 3)}%` }}
                  />
                </div>
                <p className="text-white/80">
                  ${(FUNDRAISING_GOAL - FUNDRAISING_RAISED).toLocaleString()} still needed to secure a lease
                </p>
                <p className="text-white/60 text-sm">
                  After the fantastic support of the Green Tie Gala in 2025 and our Autism Gala
                </p>
              </div>
            </div>

            <a href="https://donate.autismacceptance.ky/" target="_blank" rel="noopener noreferrer">
              <Button className="bg-[hsl(346,100%,65%)] hover:bg-[hsl(346,100%,55%)] text-white" size="lg">
                <Heart className="mr-2 h-5 w-5" />
                Donate Now
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Board Members */}
      <section id="board" className="section-spacing scroll-mt-20">
        <div className="container-main">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-[hsl(27,100%,90%)] text-[hsl(27,100%,40%)]">Leadership</Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Our Board</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Our board consists entirely of autism parents who understand firsthand the challenges 
              and joys of the autism journey.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            {boardMembers.map((member) => (
              <Card key={member.name} className="card-base text-center">
                <CardContent className="pt-8">
                  {member.image ? (
                    <img 
                      src={member.image} 
                      alt={member.name}
                      className="w-32 h-32 rounded-full object-cover mx-auto mb-4 border-4 border-[hsl(346,85%,90%)]"
                    />
                  ) : (
                    <div className="w-32 h-32 rounded-full bg-[hsl(346,85%,90%)] flex items-center justify-center mx-auto mb-4">
                      <Users className="h-12 w-12 text-[hsl(346,85%,45%)]" />
                    </div>
                  )}
                  <h3 className="text-xl font-bold">{member.name}</h3>
                  <p className="text-[hsl(346,85%,45%)] font-medium mb-4">{member.role}</p>
                  <p className="text-muted-foreground text-sm">{member.bio}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          {/* Contact Us CTA */}
          <div className="text-center">
            <a href="mailto:admin@autismacceptance.ky">
              <Button className="btn-primary" size="lg">
                <Mail className="h-5 w-5 mr-2" />
                Contact Us
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Volunteers */}
      <section className="section-spacing bg-muted/30">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src={COMMUNITY_IMG_2} 
                alt="Autism Acceptance community" 
                className="rounded-2xl shadow-lg w-full"
              />
            </div>
            <div>
              <Badge className="mb-4 bg-[hsl(346,100%,90%)] text-[hsl(346,100%,45%)]">Our Team</Badge>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Powered by Compassionate Volunteers
              </h2>
              <p className="text-muted-foreground mb-6">
                Our events and initiatives are made possible by dedicated volunteers who share our 
                vision of acceptance and inclusion. From sensory-friendly family events to awareness 
                campaigns, our volunteers are the heart of our community.
              </p>
              <Link to="/volunteer">
                <Button className="btn-primary">
                  Become a Volunteer
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="section-spacing">
        <div className="container-main text-center">
          <h2 className="text-2xl font-bold mb-4">Get in Touch</h2>
          <p className="text-muted-foreground mb-6">
            Have questions? We would love to hear from you.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="mailto:hello@autismacceptance.ky">
              <Button variant="outline">hello@autismacceptance.ky</Button>
            </a>
            <a href="mailto:admin@autismacceptance.ky">
              <Button variant="outline">admin@autismacceptance.ky</Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
